/**
 * F17 T17.1.5 — hook fetchujący listę pól modułu docelowego dla ConditionEditor
 * (i F.3 RequiredFieldsPicker — pola wymagane per krok decyzyjny).
 *
 * Fields są używane jako lista dropdownów "wybierz pole" w ConditionEditor —
 * admin buduje warunek na bazie faktycznej struktury modułu (nie wpisuje
 * fieldName z pamięci).
 *
 * **T17.1.9 — trzy źródła pól wg prefiksu kodu modułu (`moduleSlug` = qualified
 * `targetModuleCode` procesu):**
 *   - `custom.<slug>` → GET /api/modules/<slug>/fields (pola custom modułu;
 *     fieldType normalizowany do słownika warunków — patrz niżej).
 *   - `crm_core.documents` (F.3) → obszar Dokumenty ma JEDEN kod modułu dla 7 klas
 *     dokumentów, ale `system_modules.slug` jest per klasa (`purchase_invoices`,
 *     `contracts`, …) — patrz `CLASS_KARTOTEKA_SLUGS`. Bez `documentClass` nie da
 *     się rozwiązać konkretnej kartoteki, więc query jest wtedy `enabled:false`
 *     zamiast walić 404 na nieistniejący slug `documents`.
 *   - inne (systemowe, np. `crm_core.contractors`) → GET /api/system-modules/<goły-slug>/fields
 *     (T17.1.10: namespace odcinany — endpoint matchuje płaski slug z `system_modules`,
 *     np. `crm_core.contractors` → `contractors`; bez odcięcia leciał 404 → „brak pól").
 *
 * TanStack Query cache na 5 minut — definicja pól modułu rzadko się zmienia.
 */

import { type DocumentClass, HANDEL_TARGET_MODULE_CODE } from '@saas/shared';
import { queryOptions, useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { CLASS_KARTOTEKA_SLUGS } from '@/lib/documents/class-kartoteka-slug';

/**
 * Kod modułu target dla obszaru Dokumenty — jeden kod, 7 klas (patrz nagłówek pliku).
 * Eksportowany — współdzielony z `useNumberingBaseFieldOptions` (edytor pola daty numeracji),
 * żeby literał `'crm_core.documents'` nie powielał się w dwóch plikach.
 */
export const DOCUMENTS_TARGET_MODULE_CODE = 'crm_core.documents';

/**
 * Pola dokumentu z Symfonii Handel dostępne w warunkach routingu.
 *
 * To DOKŁADNIE zawartość rezerwacji identyfikatora (`hm_document_links`) — i nic ponad to.
 * Silnik procesów nie wychodzi po dane do Symfonii (transakcja Postgresa; niedostępny ERP nie
 * może blokować akceptacji), więc warunek na polu spoza tej listy nie miałby czego czytać.
 *
 * Nazwy pól są identyczne z kolumnami wiersza, bo snapshot wniosku powstaje z jego spreadu.
 */
const HANDEL_CONDITION_FIELDS: ModuleField[] = [
  f('kind', 'Kartoteka', 'text', 1),
  f('documentNumber', 'Numer dokumentu', 'text', 2),
  f('typeCode', 'Typ dokumentu', 'text', 3),
  f('series', 'Seria', 'text', 4),
  f('issueDate', 'Data wystawienia', 'date', 5),
  f('contractorName', 'Kontrahent', 'text', 6),
  f('netValue', 'Wartość netto', 'numeric', 7),
  f('grossValue', 'Wartość brutto', 'numeric', 8),
  f('currencyCode', 'Waluta', 'text', 9),
  f('inBuffer', 'W buforze', 'boolean', 10),
];

function f(slug: string, name: string, fieldType: string, sortOrder: number): ModuleField {
  return {
    id: `handel-${slug}`,
    slug,
    name,
    fieldType,
    isRequired: false,
    sortOrder,
    description: null,
  };
}

export interface ModuleField {
  id: string;
  slug: string;
  name: string;
  /** Typ pola z F12 FieldRegistry: text/number/boolean/dictionary/date/etc. */
  fieldType: string;
  isRequired: boolean;
  sortOrder: number;
  description: string | null;
  /**
   * Czy pole `reference` przechowuje WIELE rekordów. Zwraca je tylko katalog pól kartotek
   * systemowych (`/system-modules/<slug>/fields`); pola modułów własnych i lista Handel go nie mają.
   */
  relationMultiple?: boolean;
}

interface FieldsResponse {
  fields: ModuleField[];
}

/**
 * Mapowanie typów pól custom modułu (F12, 48 typów) → słownik typów, na który
 * dispatcuje `FieldValueInput` (integer/numeric/boolean/date/timestamp; reszta →
 * input tekstowy). Bez tego np. pole `currency`/`number` dostałoby input tekstowy
 * zamiast liczbowego. Typy nieobecne w mapie zostają bez zmian (text fallback).
 */
const CUSTOM_FIELD_TYPE_TO_CONDITION: Record<string, string> = {
  number: 'numeric',
  decimal: 'numeric',
  currency: 'numeric',
  percent: 'numeric',
  rating: 'numeric',
  datetime: 'timestamp',
  // date → 'date' (już zgodne); checkbox → boolean
  checkbox: 'boolean',
};

function normalizeCustomFields(data: FieldsResponse): FieldsResponse {
  return {
    fields: data.fields.map((f) => ({
      ...f,
      fieldType: CUSTOM_FIELD_TYPE_TO_CONDITION[f.fieldType] ?? f.fieldType,
    })),
  };
}

/**
 * Pobiera listę pól modułu docelowego. Gdy `moduleSlug` jest pusty / null,
 * query nie odpala się (enabled=false) — UI pokazuje placeholder "wybierz najpierw moduł".
 *
 * `documentClass` — wymagany TYLKO gdy `moduleSlug === 'crm_core.documents'` (F.3
 * RequiredFieldsPicker: konfiguracja per klasa dokumentu). Ignorowany dla pozostałych
 * modułów. Brak klasy przy module dokumentowym → query disabled (patrz `enabled` niżej).
 */
export function useFieldsForModule(
  moduleSlug: string | null | undefined,
  documentClass?: DocumentClass | null,
) {
  return useQuery(fieldsForModuleQueryOptions(moduleSlug, documentClass));
}

/**
 * Opcje zapytania o pola modułu — WSPÓLNE źródło klucza i fetchera dla `useFieldsForModule`
 * oraz dla konsumentów potrzebujących kilku katalogów naraz (`useQueries`, np. wybór pola
 * rekordu-rodzica per klasa dokumentu). Dzięki jednemu kluczowi cache jest współdzielony.
 */
export function fieldsForModuleQueryOptions(
  moduleSlug: string | null | undefined,
  documentClass?: DocumentClass | null,
) {
  const isCustom = typeof moduleSlug === 'string' && moduleSlug.startsWith('custom.');
  const isDocuments = moduleSlug === DOCUMENTS_TARGET_MODULE_CODE;
  const isHandel = moduleSlug === HANDEL_TARGET_MODULE_CODE;
  return queryOptions({
    queryKey: ['process-target-fields', moduleSlug, isDocuments ? (documentClass ?? null) : null],
    queryFn: async () => {
      // Dokument z Symfonii Handel nie ma u nas kartoteki ani katalogu pól — warunki routingu
      // mogą sięgać WYŁĄCZNIE po to, co obieg zamroził przy starcie w rezerwacji identyfikatora.
      // Lista jest więc stała i wypisana wprost, bez żadnego żądania.
      if (isHandel) return { fields: HANDEL_CONDITION_FIELDS };
      if (isCustom) {
        const slug = (moduleSlug as string).slice('custom.'.length);
        const res = await api.get<FieldsResponse>(`/modules/${slug}/fields`);
        return normalizeCustomFields(res.data);
      }
      if (isDocuments) {
        // `documentClass` gwarantowany przez `enabled` niżej (query się nie odpala bez niego).
        const kartotekaSlug = CLASS_KARTOTEKA_SLUGS[documentClass as DocumentClass];
        const res = await api.get<FieldsResponse>(`/system-modules/${kartotekaSlug}/fields`);
        return res.data;
      }
      // targetModuleCode jest qualified (`crm_core.contractors`), ale slug encji w
      // `system_modules` jest goły (`contractors`). Endpoint /system-modules/[slug]
      // matchuje dokładnie po gołym slugu — bez odcięcia namespace leciał 404 i
      // ConditionEditor pokazywał „Moduł nie ma pól do warunkowania".
      const code = moduleSlug as string;
      const systemSlug = code.includes('.') ? code.slice(code.indexOf('.') + 1) : code;
      const res = await api.get<FieldsResponse>(`/system-modules/${systemSlug}/fields`);
      return res.data;
    },
    enabled: Boolean(moduleSlug) && (!isDocuments || Boolean(documentClass)),
    staleTime: 5 * 60_000,
  });
}
